import React, { useEffect, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polygon, Polyline, useMap, useMapEvents, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { getToken, isLoggedIn } from '../../services/AuthService';
import { Spin, Select, Badge, message } from 'antd';

const { Option } = Select;

// Define colors for different enterprise types
const ENTERPRISE_COLORS = {
    FARM: '#52c41a',           // green
    BUSINESS: '#13c2c2',       // cyan
    TELECOMMUNICATION: '#1890ff', // blue
    ELECTRICAL: '#faad14',      // yellow/orange
    TRANSPORT: '#722ed1',       // purple
    BUILDING: '#eb2f96'         // pink
};

const ENTERPRISE_TYPE_LABELS = {
    FARM: 'Farm',
    BUSINESS: 'Business',
    TELECOMMUNICATION: 'Telecommunication',
    ELECTRICAL: 'Electrical',
    TRANSPORT: 'Transport',
    BUILDING: 'Built Environment'
};

// Get styling based on geometry source (exact vs approximate position)
const getGeometrySourceStyle = (geometrySource) => {
    if (geometrySource === 'DAMAGE') {
        return { fillOpacity: 0.8, dashArray: null, weight: 2 };
    }
    return { fillOpacity: 0.45, dashArray: '4 2', weight: 2 };
};

// Create custom icons for different enterprise types
const createIcon = (enterpriseType, geometrySource) => {
    const color = ENTERPRISE_COLORS[enterpriseType] || '#666';
    const isExact = geometrySource === 'DAMAGE';
    const opacity = isExact ? 1.0 : 0.6;
    const border = isExact ? 'solid' : 'dashed';
    return L.divIcon({
        html: `<div style="background-color: ${color}; opacity: ${opacity}; width: 12px; height: 12px; border-radius: 50%; border: 2px ${border} white; box-shadow: 0 0 4px rgba(0,0,0,0.3);"></div>`,
        className: 'custom-marker-icon',
        iconSize: [16, 16],
        iconAnchor: [8, 8]
    });
};

// Component to handle map events and load data
const MapEventHandler = ({ onBoundsChange, onZoomChange }) => {
    const map = useMap();

    useMapEvents({
        moveend: () => {
            const bounds = map.getBounds();
            const zoom = map.getZoom();
            onBoundsChange(bounds);
            onZoomChange(zoom);
        },
        zoomend: () => {
            const bounds = map.getBounds();
            const zoom = map.getZoom();
            onBoundsChange(bounds);
            onZoomChange(zoom);
        }
    });

    // Initial load
    useEffect(() => {
        const bounds = map.getBounds();
        const zoom = map.getZoom();
        onBoundsChange(bounds);
        onZoomChange(zoom);
    }, [map, onBoundsChange, onZoomChange]);

    return null;
};

const DashboardMap = () => {
    const [geometries, setGeometries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedType, setSelectedType] = useState(null);
    const [currentZoom, setCurrentZoom] = useState(8);
    const [stats, setStats] = useState({});

    // Load geometries based on map bounds
    const loadGeometries = useCallback(async (bounds) => {
        if (!bounds) return;

        // Check if user is logged in first
        if (!isLoggedIn()) {
            setGeometries([]);
            setStats({});
            return; // Don't make API call if not logged in
        }

        try {
            setLoading(true);
            const token = getToken();

            const params = {
                minLat: bounds.getSouth(),
                maxLat: bounds.getNorth(),
                minLng: bounds.getWest(),
                maxLng: bounds.getEast(),
                limit: 2000
            };

            if (selectedType) {
                params.enterpriseType = selectedType;
            }

            const response = await axios.get(
                `${process.env.REACT_APP_API_ENDPOINT}/surveys/dashboard/damage-geometries`,
                {
                    params,
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            setGeometries(response.data || []);

            // Calculate stats
            const typeStats = {};
            response.data.forEach(item => {
                typeStats[item.enterpriseType] = (typeStats[item.enterpriseType] || 0) + 1;
            });
            setStats(typeStats);

            // Show message if too many results
            if (response.data.length === 2000) {
                message.warning('Showing maximum 2000 damages. Zoom in for more detail.');
            }

        } catch (error) {
            console.error('Error loading geometries:', error);
            message.error('Failed to load damage data');
            setGeometries([]);
        } finally {
            setLoading(false);
        }
    }, [selectedType]);

    // Auto-reload when authentication status changes
    useEffect(() => {
        if (isLoggedIn() && geometries.length === 0) {
            // User just logged in, trigger a reload
            const map = document.querySelector('.leaflet-container');
            if (map && map._leaflet_id) {
                // Trigger bounds change to reload data
                window.dispatchEvent(new Event('resize'));
            }
        }
    }, [isLoggedIn()]);

    // Parse geometry string to GeoJSON
    const parseGeometry = (geometryString) => {
        try {
            return JSON.parse(geometryString);
        } catch (e) {
            console.error('Error parsing geometry:', e);
            return null;
        }
    };

    // Render geometry based on type
    const renderGeometry = (item) => {
        const geometry = parseGeometry(item.geometry);
        if (!geometry) return null;

        const isApproximate = item.geometrySource !== 'DAMAGE';

        const popupContent = (
            <div>
                <h4>{item.damageDescription || 'Unnamed Damage'}</h4>
                {item.damageDate && (
                    <p><strong>Date:</strong> {new Date(item.damageDate).toLocaleDateString()}</p>
                )}
                {item.estimatedValue != null && (
                    <p><strong>Estimated value:</strong> {item.estimatedValue.toLocaleString()} &euro;</p>
                )}
                {item.componentName && (
                    <p><strong>Asset:</strong> {item.componentName}</p>
                )}
                <p><strong>Asset:</strong> {item.enterpriseName || 'N/A'} ({ENTERPRISE_TYPE_LABELS[item.enterpriseType] || item.enterpriseType})</p>
                <p><strong>Event:</strong> {item.eventName || 'N/A'}</p>
                {isApproximate && (
                    <p style={{ color: '#faad14', fontSize: '11px', fontStyle: 'italic' }}>
                        Position approximate
                    </p>
                )}
            </div>
        );

        const color = ENTERPRISE_COLORS[item.enterpriseType] || '#666';
        const sourceStyle = getGeometrySourceStyle(item.geometrySource, color);

        switch (geometry.type) {
            case 'Point':
                // Use CircleMarker for better performance with many points
                if (currentZoom < 10) {
                    return (
                        <CircleMarker
                            key={`damage-${item.damageId}`}
                            center={[geometry.coordinates[1], geometry.coordinates[0]]}
                            radius={5}
                            pathOptions={{
                                fillColor: color,
                                color: 'white',
                                weight: sourceStyle.weight,
                                opacity: 1,
                                fillOpacity: sourceStyle.fillOpacity,
                                dashArray: sourceStyle.dashArray
                            }}
                        >
                            <Popup>{popupContent}</Popup>
                        </CircleMarker>
                    );
                } else {
                    return (
                        <Marker
                            key={`damage-${item.damageId}`}
                            position={[geometry.coordinates[1], geometry.coordinates[0]]}
                            icon={createIcon(item.enterpriseType, item.geometrySource)}
                        >
                            <Popup>{popupContent}</Popup>
                        </Marker>
                    );
                }

            case 'Polygon':
                return (
                    <Polygon
                        key={`damage-${item.damageId}`}
                        positions={geometry.coordinates[0].map(coord => [coord[1], coord[0]])}
                        pathOptions={{
                            color: color,
                            weight: sourceStyle.weight,
                            opacity: sourceStyle.fillOpacity,
                            fillOpacity: sourceStyle.fillOpacity * 0.4,
                            dashArray: sourceStyle.dashArray
                        }}
                    >
                        <Popup>{popupContent}</Popup>
                    </Polygon>
                );

            case 'LineString':
                return (
                    <Polyline
                        key={`damage-${item.damageId}`}
                        positions={geometry.coordinates.map(coord => [coord[1], coord[0]])}
                        pathOptions={{
                            color: color,
                            weight: 3,
                            opacity: sourceStyle.fillOpacity,
                            dashArray: sourceStyle.dashArray
                        }}
                    >
                        <Popup>{popupContent}</Popup>
                    </Polyline>
                );

            default:
                return null;
        }
    };

    return (
        <div style={{ position: 'relative', height: '600px' }}>
            {/* Controls */}
            <div style={{
                position: 'absolute',
                top: 10,
                right: 10,
                zIndex: 1000,
                backgroundColor: 'white',
                padding: '10px',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
                <Select
                    style={{ width: 200 }}
                    placeholder="Filter by type"
                    allowClear
                    value={selectedType}
                    onChange={setSelectedType}
                >
                    <Option value="FARM">Farm</Option>
                    <Option value="BUSINESS">Business</Option>
                    <Option value="TELECOMMUNICATION">Telecommunication</Option>
                    <Option value="ELECTRICAL">Electrical</Option>
                    <Option value="TRANSPORT">Transport</Option>
                    <Option value="BUILDING">Built Environment</Option>
                </Select>

                {/* Stats badges */}
                <div style={{ marginTop: '10px' }}>
                    <div style={{ marginBottom: '5px', fontSize: '12px', color: '#666' }}>
                        Visible damages: {geometries.length}
                    </div>
                    {Object.entries(ENTERPRISE_COLORS).map(([type, color]) => (
                        <Badge
                            key={type}
                            count={stats[type] || 0}
                            style={{
                                backgroundColor: color,
                                marginRight: '5px'
                            }}
                            showZero
                            title={type}
                        />
                    ))}
                </div>

                {/* Geometry source legend */}
                <div style={{ marginTop: '10px', fontSize: '11px', color: '#666', borderTop: '1px solid #eee', paddingTop: '8px' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Position accuracy:</div>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                        <span style={{
                            display: 'inline-block', width: '14px', height: '14px',
                            borderRadius: '50%', backgroundColor: '#888', opacity: 0.8,
                            border: '2px solid white', marginRight: '6px', flexShrink: 0
                        }}></span>
                        Exact position
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{
                            display: 'inline-block', width: '14px', height: '14px',
                            borderRadius: '50%', backgroundColor: '#888', opacity: 0.45,
                            border: '2px dashed white', marginRight: '6px', flexShrink: 0
                        }}></span>
                        Approximate position
                    </div>
                </div>

                {currentZoom < 10 && (
                    <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                        Zoom in for detailed markers
                    </div>
                )}
            </div>

            {/* Loading indicator */}
            {loading && (
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1000
                }}>
                    <Spin size="large" />
                </div>
            )}

            {/* Map */}
            <MapContainer
                center={[45.4642, 9.1900]} // Milan coordinates
                zoom={8}
                style={{ height: '100%', width: '100%' }}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                <MapEventHandler
                    onBoundsChange={loadGeometries}
                    onZoomChange={setCurrentZoom}
                />

                {/* Render all geometries */}
                {geometries.map(renderGeometry)}
            </MapContainer>
        </div>
    );
};

export default DashboardMap;
